import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Briefcase, Users, Rocket, ShoppingBag, PenTool, Building2, GraduationCap, Heart } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const sections = [
  { id: 'overview', title: 'Overview' },
  { id: 'entrepreneurs', title: 'Entrepreneurs & Startups' },
  { id: 'small-business', title: 'Small Business Owners' },
  { id: 'marketing-teams', title: 'Marketing Teams' },
  { id: 'content-creators', title: 'Content Creators' },
  { id: 'agencies', title: 'Agencies' },
  { id: 'ecommerce', title: 'E-commerce Businesses' },
  { id: 'educators', title: 'Educators & Coaches' },
  { id: 'nonprofits', title: 'Non-profits' },
  { id: 'get-started', title: 'Ready to get started?' },
];

const beneficiaries = [
  {
    id: 'entrepreneurs',
    icon: Rocket,
    title: 'Entrepreneurs & Startups',
    color: 'purple',
    description: 'Launch faster and do more with less. SweetPilot helps you handle tasks that would normally require a full team.',
    benefits: [
      'Automate repetitive business tasks so you can focus on growth',
      'Get help with content creation, social media, and customer communications',
      'Access AI-powered insights without hiring expensive consultants',
      'Scale your operations without scaling your headcount',
    ],
  },
  {
    id: 'small-business',
    icon: Building2,
    title: 'Small Business Owners',
    color: 'blue',
    description: 'Compete with bigger players by leveraging AI to maximize your limited resources.',
    benefits: [
      'Manage social media presence across multiple platforms effortlessly',
      'Create professional marketing materials without a design team',
      'Handle customer inquiries more efficiently',
      'Generate reports and analyze business data quickly',
    ],
  },
  {
    id: 'marketing-teams',
    icon: Users,
    title: 'Marketing Teams',
    color: 'green',
    description: 'Supercharge your marketing efforts with AI employees that work alongside your team.',
    benefits: [
      'Generate campaign ideas and content at scale',
      'Create variations of ad copy for A/B testing',
      'Analyze competitor strategies and market trends',
      'Automate social media scheduling and engagement',
    ],
  },
  {
    id: 'content-creators',
    icon: PenTool,
    title: 'Content Creators',
    color: 'orange',
    description: 'Never run out of ideas and produce more content without burning out.',
    benefits: [
      'Brainstorm content ideas for blogs, videos, and social posts',
      'Repurpose long-form content into multiple formats',
      'Write engaging captions and descriptions',
      'Plan and organize content calendars efficiently',
    ],
  },
  {
    id: 'agencies',
    icon: Briefcase,
    title: 'Agencies',
    color: 'pink',
    description: 'Deliver more value to clients while improving your profit margins.',
    benefits: [
      'Handle multiple client accounts more efficiently',
      'Produce deliverables faster without sacrificing quality',
      'Offer AI-powered services as a competitive advantage',
      'Scale your agency without proportionally increasing costs',
    ],
  },
  {
    id: 'ecommerce',
    icon: ShoppingBag,
    title: 'E-commerce Businesses',
    color: 'teal',
    description: 'Optimize your online store operations and boost sales with AI assistance.',
    benefits: [
      'Write compelling product descriptions at scale',
      'Create email marketing campaigns that convert',
      'Analyze customer feedback and reviews',
      'Generate social media content showcasing products',
    ],
  },
  {
    id: 'educators',
    icon: GraduationCap,
    title: 'Educators & Coaches',
    color: 'indigo',
    description: 'Create better learning experiences and reach more students.',
    benefits: [
      'Develop course materials and lesson plans faster',
      'Create engaging educational content',
      'Automate administrative tasks',
      'Build and maintain an online presence',
    ],
  },
  {
    id: 'nonprofits',
    icon: Heart,
    title: 'Non-profits',
    color: 'red',
    description: 'Maximize your impact with limited resources by leveraging AI.',
    benefits: [
      'Create compelling fundraising campaigns',
      'Write grant proposals more efficiently',
      'Manage donor communications at scale',
      'Spread awareness through consistent social media presence',
    ],
  },
];

const relatedArticles = [
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'Plans and pricing', href: '/doc/article/pricing-and-plan' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'Can I get a demo or a free trial?', href: '/doc/article/demo-free-trial' },
  { title: 'Which AI Models does SweetPilot use?', href: '/doc/article/ai-models' },
];

const getColorClasses = (color: string, isDark: boolean) => {
  const colors: Record<string, { bg: string; icon: string }> = {
    purple: {
      bg: isDark ? 'bg-purple-500/20' : 'bg-purple-100',
      icon: isDark ? 'text-purple-400' : 'text-purple-600',
    },
    blue: {
      bg: isDark ? 'bg-blue-500/20' : 'bg-blue-100',
      icon: isDark ? 'text-blue-400' : 'text-blue-600',
    },
    green: {
      bg: isDark ? 'bg-green-500/20' : 'bg-green-100',
      icon: isDark ? 'text-green-400' : 'text-green-600',
    },
    orange: {
      bg: isDark ? 'bg-orange-500/20' : 'bg-orange-100',
      icon: isDark ? 'text-orange-400' : 'text-orange-600',
    },
    pink: {
      bg: isDark ? 'bg-pink-500/20' : 'bg-pink-100',
      icon: isDark ? 'text-pink-400' : 'text-pink-600',
    },
    teal: {
      bg: isDark ? 'bg-teal-500/20' : 'bg-teal-100',
      icon: isDark ? 'text-teal-400' : 'text-teal-600',
    },
    indigo: {
      bg: isDark ? 'bg-indigo-500/20' : 'bg-indigo-100',
      icon: isDark ? 'text-indigo-400' : 'text-indigo-600',
    },
    red: {
      bg: isDark ? 'bg-red-500/20' : 'bg-red-100',
      icon: isDark ? 'text-red-400' : 'text-red-600',
    },
  };
  return colors[color] || colors.purple;
};

export default function WhoBenefits() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('overview');

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Who Benefits from SweetPilot? - AI Employees for Every Business',
    description: 'Discover who benefits from SweetPilot AI employees: entrepreneurs, small businesses, marketing teams, content creators, agencies, e-commerce, educators, and non-profits.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/who-benefits' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id),
      }));

      const currentSection = sectionElements.find(section => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Who Benefits from SweetPilot? - AI for Every Business | SweetPilot Help Center</title>
        <meta name="description" content="Discover who benefits from SweetPilot AI employees. Perfect for entrepreneurs, small businesses, marketing teams, content creators, agencies, e-commerce, and non-profits." />
        <meta name="keywords" content="SweetPilot benefits, AI for business, AI for entrepreneurs, AI for marketing, AI for agencies, AI for e-commerce, AI for content creators, business automation" />
        <meta property="og:title" content="Who Benefits from SweetPilot? - AI for Every Business" />
        <meta property="og:description" content="AI employees for entrepreneurs, marketers, agencies, and more." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/who-benefits" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Who Benefits from SweetPilot?" />
        <meta name="twitter:description" content="AI employees for every type of business and professional." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/who-benefits" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout title="" subtitle="" showBackButton={false}>
        {/* Breadcrumbs */}
        <nav className="mb-8">
          <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            <li>
              <Link to="/docs" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
                All Collections
              </Link>
            </li>
            <li><ChevronRight className="w-4 h-4" /></li>
          <li>
            <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
              Getting started
            </Link>
          </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li>
            <Link to="/doc/collections/getting-started" className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}>
              General info
            </Link>
          </li>
          <li><ChevronRight className="w-4 h-4" /></li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Who can benefit from using SweetPilot?
          </li>
        </ol>
      </nav>

      <div className="flex gap-10">
        {/* Main Content */}
        <div className="flex-1 max-w-3xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Who can benefit from using SweetPilot?
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Discover how different professionals and businesses can leverage AI employees to transform their workflow.
            </p>
            <div className={`flex items-center gap-3 mt-4 text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                <span className="text-sm">S</span>
              </div>
              <div>
                <span>Written by </span>
                <span className={isDark ? 'text-white' : 'text-slate-900'}>Support Team</span>
              </div>
              <span>•</span>
              <span>Updated recently</span>
            </div>
          </motion.div>

          {/* Overview Section */}
          <motion.section
            id="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-10"
          >
            <p className={`text-lg leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong className={isDark ? 'text-white' : 'text-slate-900'}>SweetPilot is designed for anyone who wants to do more with less.</strong> Whether you're a solo entrepreneur wearing multiple hats or a growing team looking to scale efficiently, our AI employees can help you automate tasks, create content, and manage your business operations.
            </p>

            <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The beauty of SweetPilot is its versatility. Our AI employees adapt to your specific needs, industry, and workflow. Here's a look at who benefits most from using SweetPilot:
            </p>
          </motion.section>

          {/* Beneficiary Sections */}
          {beneficiaries.map((beneficiary, index) => {
            const Icon = beneficiary.icon;
            const colorClasses = getColorClasses(beneficiary.color, isDark);

            return (
              <motion.section
                key={beneficiary.id}
                id={beneficiary.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + (index * 0.05) }}
                className="mb-8"
              >
                <div className={`p-6 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`p-3 rounded-lg ${colorClasses.bg}`}>
                      <Icon className={`w-6 h-6 ${colorClasses.icon}`} />
                    </div>
                    <div>
                      <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {beneficiary.title}
                      </h2>
                      <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {beneficiary.description}
                      </p>
                    </div>
                  </div>

                  <ul className={`space-y-2 ml-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    {beneficiary.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${colorClasses.icon.replace('text-', 'bg-')}`}></span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.section>
            );
          })}

          {/* Get Started Section */}
          <motion.section
            id="get-started"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-10"
          >
            <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Ready to get started?
            </h2>

            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              No matter what industry you're in or what size your business is, SweetPilot can help you work smarter, not harder. Our AI employees are ready to join your team and help you achieve more.
            </p>

            <div className={`p-5 rounded-xl border ${isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
              <div className={`space-y-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <p>
                  <strong className={isDark ? 'text-white' : 'text-slate-900'}>Start with a free trial</strong> to explore how SweetPilot can transform your workflow. No credit card required.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/pricing"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      isDark
                        ? 'bg-purple-600 hover:bg-purple-500 text-white'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }`}
                  >
                    Get Started
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/pricing"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      isDark
                        ? 'bg-white/10 hover:bg-white/20 text-white'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                    }`}
                  >
                    View Pricing
                  </Link>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Divider */}
          <hr className={`my-10 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

          {/* Related Articles */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-10"
          >
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={article.title}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1
                      ? isDark ? 'border-b border-white/5' : 'border-b border-slate-100'
                      : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>{article.title}</span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>
          </motion.section>

          {/* Feedback */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className={`text-center p-8 rounded-xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
          >
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Did this answer your question?
            </p>
            <div className="flex justify-center gap-4">
              <button className="text-3xl hover:scale-110 transition-transform" title="Not helpful">
                😞
              </button>
              <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat helpful">
                😐
              </button>
              <button className="text-3xl hover:scale-110 transition-transform" title="Very helpful">
                😃
              </button>
            </div>
          </motion.div>
        </div>

        {/* Sidebar TOC */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <nav className={`border-l ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                    activeSection === section.id
                      ? isDark
                        ? 'text-white border-l-2 border-purple-500 -ml-px bg-purple-500/10'
                        : 'text-purple-600 border-l-2 border-purple-600 -ml-px bg-purple-50'
                      : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>
      </StaticPageLayout>
    </>
  );
}
